/**
 * New Flight Page
 * 
 * Container component for creating new flights.
 * Uses Component Composition with Hooks and Container/Presentation patterns.
 * 
 * Flow:
 * 1. User selects flight type (Planned / Quick)
 * 2. Planned Flight: Stepper with Flight Setup (step 1) and Review & Launch (step 2)
 * 3. Quick Flight: Placeholder for future implementation
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    // API
    useCreateFlightMutation,
    // Hooks
    useFlightSetupForm,
    // Components
    FlightTypeSelection,
    FlightSetup,
    FlightSetupFormFields,
    FlightReviewLaunch,
    // Types
    type FlightType,
    type FlightStep,
    type FlightSetupFormData,
} from '@/features/flight';

type ViewState = 'selection' | 'planned' | 'quick';

export function NewFlightPage() {
    const navigate = useNavigate();
    
    // View and step state
    const [viewState, setViewState] = useState<ViewState>('selection');
    const [currentStep, setCurrentStep] = useState<FlightStep>(1);
    
    // Flight API mutation
    const createFlightMutation = useCreateFlightMutation();
    
    // Flight Setup Form
    const { form, missionOptions, aoiOptions, userOptions, isLoadingMissions, isLoadingAOIs, isLoadingUsers } = useFlightSetupForm();
    const { handleSubmit, formState: { isValid } } = form;

    // Handlers
    const handleFlightTypeSelect = (type: FlightType) => {
        if (type === 'planned') {
            setViewState('planned');
            setCurrentStep(1);
        } else {
            setViewState('quick');
            // Quick Flight - placeholder for future
        }
    };

    const handleBackToSelection = () => {
        setViewState('selection');
        setCurrentStep(1);
    };

    const handleContinueToReview = () => {
        setCurrentStep(2);
    };

    const handleBackToSetup = () => {
        setCurrentStep(1);
    };

    const handleStepClick = (step: FlightStep) => {
        setCurrentStep(step);
    };

    const handleFlightSubmit = (data: FlightSetupFormData) => {
        console.log('Flight Setup submitted:', data);
        
        createFlightMutation.mutate(
            {
                flightName: data.flightName,
                description: data.flightDescription,
                pilotId: data.selectPilot,
                missionId: data.selectMission,
                aoiIds: data.selectAOIs,
                aiEnabled: data.aiEnabled,
            },
            {
                onSuccess: (response) => {
                    console.log('Flight created:', response);
                    navigate('/live-operation');
                },
                onError: (error) => {
                    console.error('Failed to create flight:', error);
                    alert('Failed to create flight. Please try again.');
                },
            }
        );
    };

    const handleLaunch = () => {
        // Trigger form submission
        handleSubmit(handleFlightSubmit)();
    };

    // Render selection view
    if (viewState === 'selection') {
        return (
            <div className="w-full h-full bg-slate-950 p-8 flex flex-col items-center overflow-y-auto text-slate-200 font-sans">
                <div className="w-full max-w-5xl">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-semibold text-white mb-1">Create New Flight</h1>
                        <p className="text-slate-400">Configure and launch a new drone operation</p>
                    </div>

                    {/* Flight Type Selection */}
                    <FlightTypeSelection onSelect={handleFlightTypeSelect} />
                </div>
            </div>
        );
    }

    // Render quick flight placeholder
    if (viewState === 'quick') {
        return (
            <div className="w-full h-full bg-slate-950 p-8 flex flex-col items-center overflow-y-auto text-slate-200 font-sans">
                <div className="w-full max-w-5xl">
                    {/* Header with back button */}
                    <div className="mb-8">
                        <button
                            type="button"
                            onClick={handleBackToSelection}
                            className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to selection
                        </button>
                        <h1 className="text-3xl font-semibold text-white mb-1">Quick Flight</h1>
                        <p className="text-slate-400">Launch a quick drone operation</p>
                    </div>

                    {/* Placeholder */}
                    <div className="bg-slate-900 border border-slate-700 rounded-xl p-8 w-full flex items-center justify-center h-64">
                        <p className="text-slate-500">Quick Flight layout coming soon...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Render planned flight with stepper
    return (
        <div className="w-full h-full bg-slate-950 p-8 flex flex-col items-center overflow-y-auto text-slate-200 font-sans">
            <div className="w-full max-w-5xl">
                {/* Header with back button */}
                <div className="mb-8">
                    <button
                        type="button"
                        onClick={currentStep === 1 ? handleBackToSelection : handleBackToSetup}
                        className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {currentStep === 1 ? 'Back to selection' : 'Back to Flight Setup'}
                    </button>
                    <h1 className="text-3xl font-semibold text-white mb-1">Create New Flight</h1>
                    <p className="text-slate-400">Configure and launch a new drone operation</p>
                </div>

                {/* Stepper */}
                <FlightSetup 
                    currentStep={currentStep} 
                    onStepClick={handleStepClick} 
                />

                {/* Step Content */}
                {currentStep === 1 ? (
                    <form className="bg-slate-900 border border-slate-700 rounded-xl p-8 w-full">
                        <FlightSetupFormFields
                            form={form}
                            missionOptions={missionOptions}
                            aoiOptions={aoiOptions}
                            userOptions={userOptions}
                            isValid={isValid}
                            isLoadingMissions={isLoadingMissions}
                            isLoadingAOIs={isLoadingAOIs}
                            isLoadingUsers={isLoadingUsers}
                        />
                        <div className="flex justify-end mt-8">
                            <Button 
                                type="button"
                                onClick={handleContinueToReview}
                                disabled={!isValid}
                                className="disabled:bg-slate-700 disabled:text-slate-400"
                            >
                                Continue to Pre-Flight Check
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </form>
                ) : (
                    <FlightReviewLaunch
                        onLaunch={handleLaunch}
                        isLaunching={createFlightMutation.isPending}
                    />
                )}
            </div>
        </div>
    );
}
